import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Shared interface primitives.
 *
 * Every control is a real semantic element with a label, a visible focus ring and a touch
 * target of at least 44px, so keyboard and screen-reader use come from the markup rather
 * than from added ARIA.
 */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly variant?: 'default' | 'primary' | 'ghost' | 'danger';
  readonly size?: 'default' | 'block';
};

export function Button({ variant = 'default', size = 'default', ...props }: ButtonProps) {
  return (
    <button
      type="button"
      className="ar-button"
      data-variant={variant}
      data-size={size}
      {...props}
    />
  );
}

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly label: string;
  readonly active?: boolean;
};

export function IconButton({ label, active = false, children, ...props }: IconButtonProps) {
  return (
    <button
      type="button"
      className="ar-icon-button"
      data-active={active}
      aria-label={label}
      title={label}
      {...props}
    >
      {children}
    </button>
  );
}

export function Hearts({ remaining, total = 3 }: { remaining: number; total?: number }) {
  const hearts = [];
  for (let index = 0; index < total; index += 1) {
    const empty = index >= remaining;
    hearts.push(
      <svg
        key={index}
        className="ar-heart"
        data-empty={empty}
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        {/* Filled and outlined shapes differ, so hearts never rely on colour alone. */}
        <path
          d="M12 21 3.6 12.6a5.4 5.4 0 0 1 7.6-7.6l.8.8.8-.8a5.4 5.4 0 1 1 7.6 7.6z"
          fill={empty ? 'none' : 'currentColor'}
          stroke="currentColor"
          strokeWidth={empty ? 2 : 0}
        />
      </svg>,
    );
  }
  return (
    <span className="ar-hearts" role="img" aria-label={`${remaining} of ${total} hearts remaining`}>
      {hearts}
    </span>
  );
}

export function ProgressBar({ value, max, label }: { value: number; max: number; label: string }) {
  const percent = max <= 0 ? 0 : Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div
      className="ar-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-label={label}
    >
      <div className="ar-progress__fill" style={{ width: `${percent}%` }} />
    </div>
  );
}

export function Sheet({
  title,
  children,
  labelledBy,
}: {
  title?: string;
  children: ReactNode;
  labelledBy?: string;
}) {
  return (
    <div className="ar-overlay" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
      <div className="ar-sheet">
        {title !== undefined && (
          <h2 id={labelledBy} className="ar-subtitle">
            {title}
          </h2>
        )}
        {children}
      </div>
    </div>
  );
}

export function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="ar-stat">
      <div className="ar-stat__value">{value}</div>
      <div className="ar-stat__label">{label}</div>
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="ar-row" style={{ flexDirection: 'column', gap: 'var(--ar-space-3)' }}>
      <div className="ar-spinner" />
      <p className="ar-muted" role="status">
        {label}
      </p>
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange(next: boolean): void;
  label: string;
}) {
  return (
    <button
      type="button"
      className="ar-switch"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
    />
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: readonly { readonly value: T; readonly label: string }[];
  onChange(next: T): void;
  label: string;
}) {
  return (
    <div className="ar-segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------- icons */

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
  width: 20,
  height: 20,
} as const;

export function BackIcon() {
  return (
    <svg {...iconProps}>
      <path d="M15 5 8 12l7 7" />
    </svg>
  );
}

export function RestartIcon() {
  return (
    <svg {...iconProps}>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20 4v4h-4" />
    </svg>
  );
}

export function HintIcon() {
  return (
    <svg {...iconProps}>
      <path d="M9 18h6" />
      <path d="M10 21h4" />
      <path d="M12 3a6 6 0 0 0-3.5 10.9c.5.4.8 1 .9 1.6h5.2c.1-.6.4-1.2.9-1.6A6 6 0 0 0 12 3Z" />
    </svg>
  );
}

export function FitIcon() {
  return (
    <svg {...iconProps}>
      <path d="M4 9V4h5" />
      <path d="M20 9V4h-5" />
      <path d="M4 15v5h5" />
      <path d="M20 15v5h-5" />
    </svg>
  );
}

export function PauseIcon() {
  return (
    <svg {...iconProps}>
      <path d="M9 5v14" />
      <path d="M15 5v14" />
    </svg>
  );
}

export function SettingsIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 8.9 19a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 5 8.9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}
